import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  serverTimestamp,
  query,
  collectionGroup} from 'firebase/firestore';
import { db } from '@/config/firebase';
import type { InnovationProjectModel, InnovationPriority } from './modelsInnovation';
import { User } from '@/types';

// Función helper para construir la ruta del documento de datos del proyecto
const getProjectDataPath = (countryId: string, clientId?: string | null) => {
  const safeCountry = countryId || 'GLOBAL';
  const safeClient = clientId && clientId !== 'all' ? clientId : 'unassigned';
  return `innovations/${safeCountry}/clients/${safeClient}/projects`;
};

// Función helper para construir la ruta de la subcolección de updates
const getUpdatesPath = (countryId: string, clientId: string | null | undefined, projectId: string) => {
  const projectBasePath = getProjectDataPath(countryId, clientId);
  return `${projectBasePath}/${projectId}/updates`;
};

const priorityLabelToCode = (priority: InnovationPriority): string => {
  if (priority === 'High') return '3';
  if (priority === 'Medium') return '2';
  return '1';
};

const priorityCodeToLabel = (code: string | number): InnovationPriority => {
  const normalizedCode = String(code);
  if (normalizedCode === '3') return 'High';
  if (normalizedCode === '2') return 'Medium';
  return 'Low';
};

const contextLabelToCode = (context?: string): string | undefined => {
  const normalizedContext = context?.trim().toLowerCase();

  if (normalizedContext === 'r&d') return '1';
  if (normalizedContext === 'beta tester') return '2';

  return undefined;
};

export interface InnovationDropdownConfig {
  drivers: string[];
  topicsByDriver: Record<string, string[]>;
  projectsByTopic: Record<string, string[]>;
  driverIdsByName: Record<string, string>;
  topicIdsByName: Record<string, string>;
  projectIdsByName: Record<string, string>;
}

export interface InnovationUpdateDropdownConfig {
  statuses: string[];
  departments: string[];
  plcStages: string[];
  statusIdsByName: Record<string, string>;
  departmentIdsByName: Record<string, string>;
  plcStageIdsByName: Record<string, string>;
  statusNamesById: Record<string, string>;
  departmentNamesById: Record<string, string>;
  plcStageNamesById: Record<string, string>;
}

let cachedUpdateDropdownConfigPromise: Promise<InnovationUpdateDropdownConfig> | null = null;

const getInnovationUpdateDropdownConfigCached = () => {
  if (!cachedUpdateDropdownConfigPromise) {
    cachedUpdateDropdownConfigPromise = getInnovationUpdateDropdownConfig();
  }
  return cachedUpdateDropdownConfigPromise;
};

/**
 * Obtiene la configuración de los dropdowns de innovación (Drivers, Topics, Projects)
 * desde la colección configInnovation.
 */
export async function getInnovationDropdownConfig(): Promise<InnovationDropdownConfig> {
  try {
    const driversRef = doc(db, 'configInnovation', 'dropdowns', 'section', 'drivers');
    const topicsRef = doc(db, 'configInnovation', 'dropdowns', 'section', 'topics');
    const projectsRef = doc(db, 'configInnovation', 'dropdowns', 'section', 'projects');

    const [driversSnap, topicsSnap, projectsSnap] = await Promise.all([
      getDoc(driversRef),
      getDoc(topicsRef),
      getDoc(projectsRef)
    ]);

    const driversData = driversSnap.data()?.Drivers || {};
    const topicsData = topicsSnap.data() || {};
    const projectsData = projectsSnap.data() || {};

    const drivers = Object.values(driversData) as string[];
    const topicsByDriver: Record<string, string[]> = {};
    const projectsByTopic: Record<string, string[]> = {};
    const driverIdsByName: Record<string, string> = {};
    const topicIdsByName: Record<string, string> = {};
    const projectIdsByName: Record<string, string> = {};

    for (const [driverId, driverName] of Object.entries(driversData)) {
      const driverTopics = topicsData[driverId] || {};
      const driverNameStr = driverName as string;
      driverIdsByName[driverNameStr] = driverId;
      topicsByDriver[driverNameStr] = Object.values(driverTopics) as string[];

      for (const [topicId, topicName] of Object.entries(driverTopics)) {
        const topicProjects = projectsData[topicId] || {};
        const topicNameStr = topicName as string;
        topicIdsByName[topicNameStr] = topicId;
        projectsByTopic[topicNameStr] = Object.values(topicProjects) as string[];

        for (const [projectId, projectName] of Object.entries(topicProjects)) {
          projectIdsByName[projectName as string] = projectId;
        }
      }
    }

    return { drivers, topicsByDriver, projectsByTopic, driverIdsByName, topicIdsByName, projectIdsByName };
  } catch (error) {
    console.error('Error fetching innovation dropdown config:', error);
    return {
      drivers: [],
      topicsByDriver: {},
      projectsByTopic: {},
      driverIdsByName: {},
      topicIdsByName: {},
      projectIdsByName: {}
    };
  }
}

const normalizeInnovationDropdownLabel = (value: string) => value.trim().replace(/\s+/g, ' ');

const getNextNumericId = (keys: string[]) => {
  const max = keys.reduce((acc, key) => {
    const parsed = Number.parseInt(key, 10);
    if (Number.isNaN(parsed)) return acc;
    return Math.max(acc, parsed);
  }, 0);
  return String(max + 1);
};

export async function createInnovationDriver(driverName: string): Promise<{ driverId: string }> {
  const normalizedName = normalizeInnovationDropdownLabel(driverName);
  if (!normalizedName) {
    throw new Error('Driver name is required.');
  }

  const driversRef = doc(db, 'configInnovation', 'dropdowns', 'section', 'drivers');
  const driversSnap = await getDoc(driversRef);
  const driversData = (driversSnap.data()?.Drivers || {}) as Record<string, string>;

  const existing = Object.entries(driversData).find(([, name]) => name?.trim().toLowerCase() === normalizedName.toLowerCase());
  if (existing) {
    throw new Error(`Driver "${normalizedName}" already exists.`);
  }

  const driverId = getNextNumericId(Object.keys(driversData));
  if (driversSnap.exists()) {
    await updateDoc(driversRef, { [`Drivers.${driverId}`]: normalizedName });
  } else {
    await setDoc(driversRef, { Drivers: { [driverId]: normalizedName } }, { merge: true });
  }

  return { driverId };
}

export async function createInnovationTopic(
  driverId: string,
  topicName: string
): Promise<{ topicId: string }> {
  const normalizedName = normalizeInnovationDropdownLabel(topicName);
  if (!normalizedName) {
    throw new Error('Topic name is required.');
  }
  if (!driverId) {
    throw new Error('Driver is required.');
  }

  const topicsRef = doc(db, 'configInnovation', 'dropdowns', 'section', 'topics');
  const topicsSnap = await getDoc(topicsRef);
  const topicsData = (topicsSnap.data() || {}) as Record<string, Record<string, string>>;

  const driverTopics = (topicsData[driverId] || {}) as Record<string, string>;
  const duplicate = Object.values(driverTopics).find((name) => name?.trim().toLowerCase() === normalizedName.toLowerCase());
  if (duplicate) {
    throw new Error(`Topic "${normalizedName}" already exists for this driver.`);
  }

  const allTopicIds: string[] = [];
  for (const value of Object.values(topicsData)) {
    if (!value || typeof value !== 'object') continue;
    allTopicIds.push(...Object.keys(value));
  }
  const topicId = getNextNumericId(allTopicIds);

  if (topicsSnap.exists()) {
    await updateDoc(topicsRef, { [`${driverId}.${topicId}`]: normalizedName });
  } else {
    await setDoc(topicsRef, { [driverId]: { [topicId]: normalizedName } }, { merge: true });
  }

  return { topicId };
}

export async function createInnovationProjectOption(topicId: string, projectName: string): Promise<{ projectId: string }> {
  const normalizedName = normalizeInnovationDropdownLabel(projectName);
  if (!normalizedName) {
    throw new Error('Project name is required.');
  }
  if (!topicId) {
    throw new Error('Topic is required.');
  }

  const projectsRef = doc(db, 'configInnovation', 'dropdowns', 'section', 'projects');
  const projectsSnap = await getDoc(projectsRef);
  const projectsData = (projectsSnap.data() || {}) as Record<string, Record<string, string>>;

  const topicProjects = (projectsData[topicId] || {}) as Record<string, string>;
  const duplicate = Object.values(topicProjects).find((name) => name?.trim().toLowerCase() === normalizedName.toLowerCase());
  if (duplicate) {
    throw new Error(`Project "${normalizedName}" already exists for this topic.`);
  }

  const projectId = getNextNumericId(Object.keys(topicProjects));
  if (projectsSnap.exists()) {
    await updateDoc(projectsRef, { [`${topicId}.${projectId}`]: normalizedName });
  } else {
    await setDoc(projectsRef, { [topicId]: { [projectId]: normalizedName } }, { merge: true });
  }

  return { projectId };
}

export async function renameInnovationDriver(driverId: string, newDriverName: string): Promise<void> {
  const normalizedName = normalizeInnovationDropdownLabel(newDriverName);
  if (!driverId) {
    throw new Error('Driver is required.');
  }
  if (!normalizedName) {
    throw new Error('Driver name is required.');
  }

  const driversRef = doc(db, 'configInnovation', 'dropdowns', 'section', 'drivers');
  await updateDoc(driversRef, { [`Drivers.${driverId}`]: normalizedName });
}

export async function renameInnovationTopic(
  driverId: string,
  topicId: string,
  newTopicName: string
): Promise<void> {
  const normalizedName = normalizeInnovationDropdownLabel(newTopicName);
  if (!driverId) {
    throw new Error('Driver is required.');
  }
  if (!topicId) {
    throw new Error('Topic is required.');
  }
  if (!normalizedName) {
    throw new Error('Topic name is required.');
  }

  const topicsRef = doc(db, 'configInnovation', 'dropdowns', 'section', 'topics');
  await updateDoc(topicsRef, { [`${driverId}.${topicId}`]: normalizedName });
}

export async function renameInnovationProjectOption(
  topicId: string,
  projectId: string,
  newProjectName: string
): Promise<void> {
  const normalizedName = normalizeInnovationDropdownLabel(newProjectName);
  if (!topicId) {
    throw new Error('Topic is required.');
  }
  if (!projectId) {
    throw new Error('Project is required.');
  }
  if (!normalizedName) {
    throw new Error('Project name is required.');
  }

  const projectsRef = doc(db, 'configInnovation', 'dropdowns', 'section', 'projects');
  await updateDoc(projectsRef, { [`${topicId}.${projectId}`]: normalizedName });
}

/**
 * Obtiene la configuración de los dropdowns de updates (Status, Department, PLC Stage)
 * desde la colección configInnovation.
 */
export async function getInnovationUpdateDropdownConfig(): Promise<InnovationUpdateDropdownConfig> {
  try {
    const updateRef = doc(db, 'configInnovation', 'dropdowns', 'section', 'update');
    const snapshot = await getDoc(updateRef);
    const data = snapshot.data() || {};

    const statusMap = (data.Status || {}) as Record<string, string>;
    const departmentMap = (data.Department || {}) as Record<string, string>;
    const plcStageMap = (data.PLCStage || {}) as Record<string, string>;

    const statuses = Object.values(statusMap);
    const departments = Object.values(departmentMap);
    const plcStages = Object.values(plcStageMap);

    const statusIdsByName: Record<string, string> = {};
    const departmentIdsByName: Record<string, string> = {};
    const plcStageIdsByName: Record<string, string> = {};
    const statusNamesById: Record<string, string> = {};
    const departmentNamesById: Record<string, string> = {};
    const plcStageNamesById: Record<string, string> = {};

    for (const [id, name] of Object.entries(statusMap)) {
      statusIdsByName[name] = id;
      statusNamesById[id] = name;
    }

    for (const [id, name] of Object.entries(departmentMap)) {
      departmentIdsByName[name] = id;
      departmentNamesById[id] = name;
    }

    for (const [id, name] of Object.entries(plcStageMap)) {
      plcStageIdsByName[name] = id;
      plcStageNamesById[id] = name;
    }

    return {
      statuses,
      departments,
      plcStages,
      statusIdsByName,
      departmentIdsByName,
      plcStageIdsByName,
      statusNamesById,
      departmentNamesById,
      plcStageNamesById,
    };
  } catch (error) {
    console.error('Error fetching innovation update dropdown config:', error);
    return {
      statuses: [],
      departments: [],
      plcStages: [],
      statusIdsByName: {},
      departmentIdsByName: {},
      plcStageIdsByName: {},
      statusNamesById: {},
      departmentNamesById: {},
      plcStageNamesById: {},
    };
  }
}

/**
 * Obtiene todos los proyectos de innovación filtrados por los permisos del usuario.
 */
export async function getInnovationProjects(user: User | null, countryId?: string | null, clientId?: string | null): Promise<InnovationProjectModel[]> {
  if (!user) return [];

  try {
    let allProjects: InnovationProjectModel[] = [];

    // Dado que la información del proyecto ahora reside en una subcolección 'data',
    // usamos collectionGroup para buscar en todos los documentos 'data'.
    const q = query(collectionGroup(db, 'data'));
    const snapshot = await getDocs(q);
    
    allProjects = snapshot.docs
      .filter((doc) => {
        const path = doc.ref.path;
        return path.startsWith('innovations/') && path.includes('/projects/') && doc.id === 'main';
      })
      .map(doc => {
      const raw = doc.data() as any;
      let priority: InnovationPriority = 'Medium';
      if (typeof raw.priority === 'number') {
        priority = priorityCodeToLabel(raw.priority);
      } else if (typeof raw.priority === 'string') {
        priority = raw.priority === 'High' || raw.priority === 'Medium' || raw.priority === 'Low'
          ? raw.priority as InnovationPriority
          : priorityCodeToLabel(raw.priority);
      }
      const updatesCount =
        raw.updatesCount === undefined || raw.updatesCount === null ? undefined : String(raw.updatesCount);

      return {
        id: doc.ref.parent.parent?.id,
        ...raw,
        priority,
        updatesCount,
      } as InnovationProjectModel;
    });

    // Filtramos los proyectos válidos
    allProjects = allProjects.filter(p => p.id != null);

    // Si tenemos país y cliente específico, filtramos en memoria
    if (countryId && clientId && clientId !== 'all') {
      allProjects = allProjects.filter(p => p.country === countryId && p.clientId === clientId);
    } 
    // Si solo hay un país seleccionado, filtramos por país
    else if (countryId) {
      allProjects = allProjects.filter(p => p.country === countryId);
    }

    // Si es admin/manager/innovation, devolvemos lo encontrado
    if (user.role === 'manager' || user.role === 'innovation' || user.role === 'innovation++') {
      return allProjects;
    }

    // Si es comercial o regional, filtramos por sus permisos
    return allProjects.filter((project) => {
      // 1. Verificar acceso al país
      const canAccessCountry = user.access_permissions?.countries?.includes(project.country);
      
      // 2. Verificar acceso al cliente (si tiene clientId)
      let canAccessClient = true;
      if (project.clientId && user.access_permissions?.clients) {
        canAccessClient = user.access_permissions.clients.includes(project.clientId);
      }

      return canAccessCountry && canAccessClient;
    });
  } catch (error) {
    console.error('Error fetching innovation projects:', error);
    throw error;
  }
}

/**
 * Crea un nuevo proyecto de innovación
 */
export async function createInnovationProject(project: InnovationProjectModel, userEmail: string): Promise<string> {
  try {
    const projectId = project.id || `INN-${Date.now()}`;
    const path = getProjectDataPath(project.country, project.clientId);
    const projectDocRef = doc(db, path, projectId);
    
    // Creamos el documento 'main' dentro de la subcolección 'data'
    const dataDocRef = doc(projectDocRef, 'data', 'main');

    const priorityCode = priorityLabelToCode(project.priority);
    const contextCode = contextLabelToCode(project.context);

    const projectData: any = {
      ...project,
      priority: priorityCode,
      contextId: contextCode,
      id: projectId,
      syncedToSAP: false,
      createdAt: serverTimestamp(),
      createdBy: userEmail,
      updatedAt: serverTimestamp(),
      updatedBy: userEmail,
      updatesCount: '0'
    };

    // Eliminar campos undefined para evitar errores de Firestore
    Object.keys(projectData).forEach((key) => {
      if ((projectData as any)[key] === undefined) {
        delete (projectData as any)[key];
      }
    });

    // Guardamos un doc vacío en el padre para que la consola de Firebase lo muestre
    // Esto es un patrón común en Firestore para que la colección no aparezca cursiva
    await setDoc(projectDocRef, {
      projectId: projectId,
      createdAt: serverTimestamp()
    });

    // Guardamos la información real en data/main
    await setDoc(dataDocRef, projectData);
    return projectId;
  } catch (error) {
    console.error('Error creating innovation project:', error);
    throw error;
  }
}

/**
 * Actualiza un proyecto existente (incluyendo sus updates)
 */
export async function updateInnovationProject(
  project: InnovationProjectModel,
  userEmail: string,
  previousProject?: InnovationProjectModel | null
): Promise<void> {
  if (!project.id) throw new Error('Project ID is required for update');

  try {
    const nextPath = getProjectDataPath(project.country, project.clientId);
    const previousPath = previousProject
      ? getProjectDataPath(previousProject.country, previousProject.clientId)
      : nextPath;
    const dataDocRef = doc(db, nextPath, project.id, 'data', 'main');
    
    const priorityCode = priorityLabelToCode(project.priority);
    const contextCode = contextLabelToCode(project.context);

    const updateData: any = {
      ...project,
      priority: priorityCode,
      contextId: contextCode ?? deleteField(),
      syncedToSAP: false,
      updatedAt: serverTimestamp(),
      updatedBy: userEmail,
    };

    if (updateData.updatesCount !== undefined && updateData.updatesCount !== null) {
      updateData.updatesCount = String(updateData.updatesCount);
    }

    // Eliminar campos undefined para evitar errores de Firestore
    Object.keys(updateData).forEach((key) => {
      if ((updateData as any)[key] === undefined) {
        delete (updateData as any)[key];
      }
    });

    if (previousPath !== nextPath && previousProject?.id) {
      const previousProjectDocRef = doc(db, previousPath, previousProject.id);
      const previousDataDocRef = doc(previousProjectDocRef, 'data', 'main');
      const nextProjectDocRef = doc(db, nextPath, project.id);

      await setDoc(nextProjectDocRef, {
        projectId: project.id,
        createdAt: serverTimestamp()
      }, { merge: true });
      await setDoc(dataDocRef, updateData, { merge: true });

      const previousUpdatesRef = collection(db, getUpdatesPath(previousProject.country, previousProject.clientId, previousProject.id));
      const previousUpdatesSnapshot = await getDocs(previousUpdatesRef);
      for (const updateSnapshot of previousUpdatesSnapshot.docs) {
        const nextUpdateRef = doc(db, getUpdatesPath(project.country, project.clientId, project.id), updateSnapshot.id);
        await setDoc(nextUpdateRef, updateSnapshot.data(), { merge: true });
        await deleteDoc(updateSnapshot.ref);
      }

      await deleteDoc(previousDataDocRef);
      await deleteDoc(previousProjectDocRef);
      return;
    }

    await updateDoc(dataDocRef, updateData);
  } catch (error) {
    console.error(`Error updating innovation project ${project.id}:`, error);
    throw error;
  }
}

/**
 * Obtiene todos los updates de un proyecto específico
 */
export async function getInnovationProjectUpdates(project: InnovationProjectModel): Promise<any[]> {
  if (!project.id) return [];

  try {
    const updatesPath = getUpdatesPath(project.country, project.clientId, project.id);
    const updatesRef = collection(db, updatesPath);
    // Ordenamos por fecha descendente (más reciente primero)
    // Asumimos que guardamos un campo timestamp o la fecha original para ordenar, o lo hacemos en memoria
    const snapshot = await getDocs(updatesRef);

    const allUpdates = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    const updateConfig = await getInnovationUpdateDropdownConfigCached();
    const mappedUpdates = allUpdates.map((upd: any) => {
      const mapped = { ...upd };

      if (!mapped.newStatus && mapped.idStatus && updateConfig.statusNamesById[mapped.idStatus]) {
        mapped.newStatus = updateConfig.statusNamesById[mapped.idStatus];
      }

      if (!mapped.pendingDepartment && mapped.idDepartment && updateConfig.departmentNamesById[mapped.idDepartment]) {
        mapped.pendingDepartment = updateConfig.departmentNamesById[mapped.idDepartment];
      }

      if (!mapped.plcStage && mapped.idPLC && updateConfig.plcStageNamesById[mapped.idPLC]) {
        mapped.plcStage = updateConfig.plcStageNamesById[mapped.idPLC];
      }

      if (!mapped.idStatus && mapped.newStatus && updateConfig.statusIdsByName[mapped.newStatus]) {
        mapped.idStatus = updateConfig.statusIdsByName[mapped.newStatus];
      }

      if (!mapped.idDepartment && mapped.pendingDepartment && updateConfig.departmentIdsByName[mapped.pendingDepartment]) {
        mapped.idDepartment = updateConfig.departmentIdsByName[mapped.pendingDepartment];
      }

      if (!mapped.idPLC && mapped.plcStage && updateConfig.plcStageIdsByName[mapped.plcStage]) {
        mapped.idPLC = updateConfig.plcStageIdsByName[mapped.plcStage];
      }

      return mapped;
    });

    // Ordenar en memoria si updateDate es string (formato 'es-ES')
    return mappedUpdates.sort((a: any, b: any) => {
      // Intentar convertir la cadena de fecha a algo comparable si es necesario, 
      // o confiar en createdAt si lo vamos a añadir.
      // Por simplicidad, devolver tal cual o usar un createdAt si lo añadimos en la creación
      if (b.createdAt && a.createdAt) {
         return b.createdAt.toMillis() - a.createdAt.toMillis();
      }
      return 0;
    });
  } catch (error) {
    console.error(`Error fetching updates for project ${project.id}:`, error);
    return [];
  }
}

/**
 * Añade o actualiza un update en la subcolección del proyecto
 */
export async function saveInnovationUpdate(project: InnovationProjectModel, updateData: any, userEmail: string): Promise<void> {
  if (!project.id) throw new Error('Project ID is required');

  try {
    const updatesPath = getUpdatesPath(project.country, project.clientId, project.id);
    
    // Si el update ya tiene un ID (estamos editando), lo usamos. Si no, generamos uno nuevo
    // Usamos el originalUpdateDate como ID si existe (es lo que usábamos para identificarlo antes)
    // o generamos uno nuevo limpio
    const updateId = updateData.id || `UPD-${Date.now()}`;
    
    const updateRef = doc(db, updatesPath, updateId);
    
    const finalUpdateData: any = {
      ...updateData,
      id: updateId,
      syncedToSAP: false,
      updatedAt: serverTimestamp(),
      updatedBy: userEmail,
    };

    // Si es nuevo, añadimos createdAt
    if (!updateData.id) {
      finalUpdateData.createdAt = serverTimestamp();
      finalUpdateData.createdBy = userEmail;
    }

    if (typeof updateData.updatedByEmail === 'string' && updateData.updatedByEmail.trim()) {
      finalUpdateData.updatedByEmail = updateData.updatedByEmail.trim();
    }

    if (!updateData.id && typeof updateData.createdByEmail === 'string' && updateData.createdByEmail.trim()) {
      finalUpdateData.createdByEmail = updateData.createdByEmail.trim();
    }

    // Limpiar undefined
    Object.keys(finalUpdateData).forEach((key) => {
      if ((finalUpdateData as any)[key] === undefined) {
        delete (finalUpdateData as any)[key];
      }
    });

    await setDoc(updateRef, finalUpdateData, { merge: true });
    
    // Actualizar el proyecto padre con el conteo de updates y el nuevo status si lo hay
    const projectPath = getProjectDataPath(project.country, project.clientId);
    const dataDocRef = doc(db, projectPath, project.id, 'data', 'main');
    const currentUpdates = await getInnovationProjectUpdates(project);
    
    const parentUpdateData: any = {
      updatesCount: String(currentUpdates.length),
      syncedToSAP: false,
      updatedAt: serverTimestamp(),
      updatedBy: userEmail
    };

    // Si el update incluye un nuevo estado, actualizar también el estado a nivel de proyecto
    if (finalUpdateData.newStatus) {
      parentUpdateData.status = finalUpdateData.newStatus;
      if (finalUpdateData.idStatus) {
        parentUpdateData.idStatus = finalUpdateData.idStatus;
      }
    }

    await updateDoc(dataDocRef, parentUpdateData);

  } catch (error) {
    console.error(`Error saving update for project ${project.id}:`, error);
    throw error;
  }
}

/**
 * Elimina un update de la subcolección del proyecto
 */
export async function deleteInnovationUpdate(project: InnovationProjectModel, updateId: string): Promise<void> {
  if (!project.id || !updateId) throw new Error('Project ID and Update ID are required');

  try {
    const updatesPath = getUpdatesPath(project.country, project.clientId, project.id);
    const updateRef = doc(db, updatesPath, updateId);
    await deleteDoc(updateRef);
    
    // Actualizar el conteo en el proyecto padre
    const projectPath = getProjectDataPath(project.country, project.clientId);
    const dataDocRef = doc(db, projectPath, project.id, 'data', 'main');
    const currentUpdates = await getInnovationProjectUpdates(project);
    
    await updateDoc(dataDocRef, {
      updatesCount: String(currentUpdates.length),
      updatedAt: serverTimestamp()
    });
    
  } catch (error) {
    console.error(`Error deleting update ${updateId} for project ${project.id}:`, error);
    throw error;
  }
}

/**
 * Elimina un proyecto de innovación
 */
export async function deleteInnovationProject(project: InnovationProjectModel): Promise<void> {
  if (!project.id) throw new Error('Project ID is required for deletion');

  try {
    const path = getProjectDataPath(project.country, project.clientId);
    const projectDocRef = doc(db, path, project.id);
    const dataDocRef = doc(projectDocRef, 'data', 'main');
    
    // Idealmente deberíamos borrar también los documentos de la subcolección 'updates'
    // Para simplificar, si no son muchos, los leemos y borramos
    const updates = await getInnovationProjectUpdates(project);
    for (const update of updates) {
      if (update.id) {
        await deleteDoc(doc(db, getUpdatesPath(project.country, project.clientId, project.id), update.id));
      }
    }
    
    // Borramos el doc de data
    await deleteDoc(dataDocRef);
    // Borramos el doc padre
    await deleteDoc(projectDocRef);
  } catch (error) {
    console.error(`Error deleting innovation project ${project.id}:`, error);
    throw error;
  }
}
